import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import useGoogleAuth from '~/components/useGoogleAuth';
import { IS_CLIENT_SIDE, GOOGLE_CLIENT_ID } from '~/constants';
import analytics from '~/services/analytics';
import { loginSelectors } from '~/services/login';
import { signupSelectors } from '~/services/signup';

const action = 'onetap';

export type WindowWithGoogle = globalThis.Window & {
	google?: {
		accounts: {
			id: {
				initialize: (params: {
					client_id: string;
					callback: (response: { credential: string }) => void;
				}) => void;
				renderButton: (
					element: HTMLElement | null,
					options: {
						type?: 'standard' | 'icon';
						theme?: 'outline' | 'filled_blue' | 'filled_black';
						size?: 'large' | 'medium' | 'small';
						width?: number;
						text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
						shape?: 'rectangular' | 'pill';
						logo_alignment?: 'left' | 'center';
						locale?: string;
					},
				) => void;
			};
		};
	};
};

interface Props {
	hasGoogleGsiScriptLoaded: boolean;
}
const useGoogleOneTap = ({ hasGoogleGsiScriptLoaded }: Props) => {
	const postLoginRedirectUrl = useSelector(
		loginSelectors.getPostLoginRedirectUrl,
	);
	const postSignupRedirectUrl = useSelector(
		signupSelectors.getPostSignupRedirectUrl,
	);

	const redirectTo = postSignupRedirectUrl || postLoginRedirectUrl;

	const { onSubmit, isAuth } = useGoogleAuth({
		action,
		redirectTo,
	});
	const handleCredentialResponse = useCallback(
		response => {
			analytics.track(analytics.events.GOOGLE_ONE_TAP_CLICKED);
			onSubmit({
				token: response.credential,
				onetapSignUp: postSignupRedirectUrl !== null,
			});
		},
		[onSubmit, postSignupRedirectUrl],
	);

	useEffect(() => {
		if (hasGoogleGsiScriptLoaded && IS_CLIENT_SIDE && !isAuth) {
			const windowWithGoogle = window as WindowWithGoogle;

			// https://developers.google.com/identity/gsi/web/guides/use-one-tap-js-api
			// if one tap is not appearing, clear g_state cookie
			windowWithGoogle.google?.accounts.id.initialize({
				client_id: GOOGLE_CLIENT_ID as string,
				callback: handleCredentialResponse,
			});
		}
	}, [hasGoogleGsiScriptLoaded, handleCredentialResponse, isAuth]);

	return null;
};

export default useGoogleOneTap;
